/**
 * Hala CRM - Layout Component
 * Role-based layout with dynamic sidebar
 */

import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, FileText, Inbox, ClipboardList, Users, Layers, Shield,
  UserCheck, LogOut, Menu, X, Bell, ChevronDown, User, Monitor, Settings
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primary: '#1B3A6B',
  primaryLight: '#2A4A7A',
  gold: '#C5A572',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  success: '#10B981',
  error: '#EF4444',
};

const ICONS = {
  LayoutDashboard, FileText, Inbox, ClipboardList, Users, Layers, Shield, UserCheck, Monitor, Settings
};

const ROLE_LABELS = {
  admin: { label: 'مدير النظام', color: '#DC2626' },
  supervisor: { label: 'مشرف', color: '#2563EB' },
  consultant: { label: 'مستشار', color: '#059669' },
  group_user: { label: 'مستخدم مجموعة', color: '#D97706' },
};

function getCRMAuth() {
  const token = localStorage.getItem('crm_token');
  const userStr = localStorage.getItem('crm_user');
  if (!token || !userStr) return null;
  try {
    return { token, user: JSON.parse(userStr) };
  } catch {
    return null;
  }
}

function getCRMHeaders() {
  const auth = getCRMAuth();
  return auth ? { Authorization: `Bearer ${auth.token}` } : {};
}

export default function CRMLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [navigation, setNavigation] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getCRMAuth();
    if (!auth) {
      navigate('/crm/login');
      return;
    }

    setUser(auth.user);

    // Fetch navigation and allowed pages
    axios.get(`${API}/crm-auth/navigation`, { headers: getCRMHeaders() })
      .then(res => {
        setNavigation(res.data.navigation || []);
        
        // Check if current page is allowed for this role
        const allowedPages = res.data.allowed_pages || [];
        const currentPath = location.pathname;
        
        // Check if path starts with any allowed page
        const isAllowed = allowedPages.some(allowed => 
          currentPath === allowed || currentPath.startsWith(allowed + '/')
        );
        
        // If not allowed, redirect to first allowed page
        if (!isAllowed && allowedPages.length > 0) {
          navigate(allowedPages[0]);
        }
      })
      .catch(() => {
        navigate('/crm/login');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate, location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    navigate('/crm/login');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: COLORS.bg,
        color: COLORS.textMuted,
      }}>
        جاري التحميل...
      </div>
    );
  }

  const roleStyle = ROLE_LABELS[user?.role_id] || { label: user?.role_id, color: COLORS.textMuted };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: COLORS.bg, direction: 'rtl' }}>
      {/* Sidebar - على اليمين */}
      <aside style={{
        width: sidebarOpen ? '260px' : '0',
        backgroundColor: COLORS.primary,
        transition: 'width 0.3s',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        order: 1,
      }}>
        {/* Logo */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: COLORS.gold }}>
            نظام إدارة الطلبات
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
            هلا
          </p>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
          {navigation.map((item) => {
            const Icon = ICONS[item.icon] || FileText;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  marginBottom: '4px',
                  borderRadius: '10px',
                  backgroundColor: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                  color: isActive ? COLORS.white : 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: isActive ? 600 : 400,
                  transition: 'all 0.2s',
                }}
              >
                <Icon size={20} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User Info */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: COLORS.gold,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: COLORS.primary,
              fontWeight: 700,
            }}>
              {user?.name_ar?.charAt(0) || 'م'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 600,
                color: COLORS.white,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {user?.name_ar}
              </p>
              <span style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: `${roleStyle.color}30`,
                color: roleStyle.color,
                fontSize: '11px',
                fontWeight: 500,
              }}>
                {roleStyle.label}
              </span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              color: 'rgba(255,255,255,0.8)',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <LogOut size={16} />
            تسجيل الخروج
          </button>
        </div>
      </aside>

      {/* Main Content - على اليسار */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', order: 2 }}>
        {/* Header */}
        <header style={{
          backgroundColor: COLORS.white,
          borderBottom: `1px solid ${COLORS.border}`,
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              padding: '8px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: COLORS.bgDark,
              cursor: 'pointer',
            }}
          >
            {sidebarOpen ? <X size={20} color={COLORS.textMuted} /> : <Menu size={20} color={COLORS.textMuted} />}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button style={{
              padding: '8px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: COLORS.bgDark,
              cursor: 'pointer',
              position: 'relative',
            }}>
              <Bell size={20} color={COLORS.textMuted} />
            </button>
          </div>
        </header>

        {/* Content */}
        <main style={{ flex: 1, padding: '24px', overflowY: 'auto', direction: 'rtl' }}>
          <Outlet context={{ user, getCRMHeaders }} />
        </main>
      </div>
    </div>
  );
}

// Export helper for child components
export { getCRMAuth, getCRMHeaders };
